import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Film, Image as ImageIcon, Download, Loader2, Volume2, VolumeX, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { mediaStudioTexts } from './mediaStudioTexts';

const W = 720, H = 1280;

const pickMime = () => ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
  .find(m => window.MediaRecorder && MediaRecorder.isTypeSupported(m));

const drawMedia = (ctx, el, x, y, w, h, fit) => {
  const sw = el.videoWidth || el.naturalWidth || 1, sh = el.videoHeight || el.naturalHeight || 1;
  const r = fit === 'contain' ? Math.min(w / sw, h / sh) : Math.max(w / sw, h / sh);
  const dw = sw * r, dh = sh * r;
  ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
  ctx.drawImage(el, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.restore();
};

const Slot = ({ label, slot, setSlot, L, dark }) => {
  const inputRef = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const url = URL.createObjectURL(f);
    setSlot({ kind: f.type.startsWith('video') ? 'video' : 'image', url, name: f.name, fit: 'cover' });
    e.target.value = '';
  };
  return (
    <div className={`rounded-2xl border p-3 ${dark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/10'}`} data-testid={`ve-slot-${label}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold">{L[label]}</span>
        {slot && <button onClick={() => setSlot(null)} className="p-1 rounded-full bg-red-500/20" data-testid={`ve-slot-${label}-clear`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>}
      </div>
      {slot ? (
        <div className="flex items-center gap-2 text-xs">
          {slot.kind === 'video' ? <Film className="w-4 h-4 text-fuchsia-400" /> : <ImageIcon className="w-4 h-4 text-fuchsia-400" />}
          <span className="truncate flex-1">{slot.name}</span>
          <select value={slot.fit} onChange={e => setSlot({ ...slot, fit: e.target.value })} className={`text-[11px] rounded-lg px-1.5 py-1 ${dark ? 'bg-black/40' : 'bg-white'}`} data-testid={`ve-slot-${label}-fit`}>
            <option value="cover">{L.cover}</option><option value="contain">{L.contain}</option>
          </select>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => { inputRef.current.accept = 'video/*'; inputRef.current.click(); }} className={`py-2 rounded-xl border border-dashed text-[11px] flex items-center justify-center gap-1 ${dark ? 'border-white/30' : 'border-black/30'}`} data-testid={`ve-slot-${label}-add-video`}><Upload className="w-3.5 h-3.5" />{L.addVideo}</button>
          <button onClick={() => { inputRef.current.accept = 'image/*'; inputRef.current.click(); }} className={`py-2 rounded-xl border border-dashed text-[11px] flex items-center justify-center gap-1 ${dark ? 'border-white/30' : 'border-black/30'}`} data-testid={`ve-slot-${label}-add-image`}><Upload className="w-3.5 h-3.5" />{L.addImage}</button>
        </div>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={onFile} />
    </div>
  );
};

export default function VideoEditorModal({ open, onClose }) {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const L = mediaStudioTexts(language);
  const [top, setTop] = useState(null);
  const [bottom, setBottom] = useState(null);
  const [text, setText] = useState('');
  const [color, setColor] = useState('#ffffff');
  const [split, setSplit] = useState(50);
  const [muted, setMuted] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [progress, setProgress] = useState(0);
  const [outUrl, setOutUrl] = useState(null);
  const canvasRef = useRef(null);
  const topEl = useRef(null), botEl = useRef(null), rafRef = useRef(null);

  const mkEl = (slot, ref) => {
    if (!slot) { ref.current = null; return; }
    if (slot.kind === 'video') {
      const v = document.createElement('video'); v.src = slot.url; v.muted = true; v.loop = true; v.playsInline = true; v.crossOrigin = 'anonymous'; v.play().catch(() => {});
      ref.current = v;
    } else { const i = new Image(); i.src = slot.url; ref.current = i; }
  };
  useEffect(() => { mkEl(top, topEl); }, [top]);
  useEffect(() => { mkEl(bottom, botEl); }, [bottom]);

  const paint = (ctx) => {
    ctx.fillStyle = '#000'; ctx.fillRect(0, 0, W, H);
    const hTop = Math.round(H * split / 100);
    if (topEl.current && top) drawMedia(ctx, topEl.current, 0, 0, W, hTop, top.fit);
    if (botEl.current && bottom) drawMedia(ctx, botEl.current, 0, hTop, W, H - hTop, bottom.fit);
    if (text.trim()) {
      ctx.save(); ctx.font = 'bold 54px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 14; ctx.lineWidth = 8; ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      const lines = text.split('\n').slice(0, 3);
      lines.forEach((ln, i) => { const y = hTop + (i - (lines.length - 1) / 2) * 64; ctx.strokeText(ln, W / 2, y); ctx.fillStyle = color; ctx.fillText(ln, W / 2, y); });
      ctx.restore();
    }
  };

  useEffect(() => {
    if (!open) return;
    const loop = () => { const c = canvasRef.current; if (c) paint(c.getContext('2d')); rafRef.current = requestAnimationFrame(loop); };
    loop();
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line
  }, [open, top, bottom, text, color, split]);

  if (!open) return null;

  const render = async () => {
    const vids = [top, bottom].filter(s => s?.kind === 'video');
    if (!vids.length) { toast.error(L.needTwo); return; }
    const mime = pickMime();
    if (!mime) { toast.error(L.unsupported); return; }
    setRendering(true); setOutUrl(null); setProgress(0);
    try {
      const canvas = canvasRef.current;
      const stream = canvas.captureStream(30);
      const vEls = [topEl.current, botEl.current].filter(e => e && e.tagName === 'VIDEO');
      let dur = Math.min(60, Math.max(...vEls.map(v => v.duration || 0)) || 10);
      if (!muted) {
        try {
          const ac = new (window.AudioContext || window.webkitAudioContext)(); const dest = ac.createMediaStreamDestination();
          vEls.forEach(v => { v.muted = false; v.volume = 1; ac.createMediaElementSource(v).connect(dest); });
          dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
        } catch { /* audio mixing unsupported: silent export */ }
      }
      vEls.forEach(v => { v.loop = false; v.currentTime = 0; v.play().catch(() => {}); });
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 5_000_000 });
      const chunks = []; rec.ondataavailable = e => e.data.size && chunks.push(e.data);
      const done = new Promise(res => { rec.onstop = res; });
      rec.start(250);
      const t0 = Date.now();
      await new Promise(res => { const tick = () => { const p = (Date.now() - t0) / 1000; setProgress(Math.min(100, Math.round(p / dur * 100))); if (p >= dur) res(); else setTimeout(tick, 200); }; tick(); });
      rec.stop(); await done;
      vEls.forEach(v => { v.loop = true; v.muted = true; v.play().catch(() => {}); });
      const blob = new Blob(chunks, { type: mime.split(';')[0] });
      setOutUrl(URL.createObjectURL(blob));
      toast.success(L.done);
    } catch (e) { toast.error(L.unsupported); }
    finally { setRendering(false); }
  };

  const ext = (pickMime() || 'video/webm').includes('mp4') ? 'mp4' : 'webm';
  const inputCls = `w-full rounded-xl px-3 py-2 text-sm border ${dark ? 'bg-black/40 border-white/10 text-white' : 'bg-white border-black/10 text-gray-900'}`;

  return (
    <div className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center" data-testid="video-editor-modal">
      <div className={`w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl border max-h-[92vh] overflow-y-auto ${dark ? 'bg-gradient-to-b from-gray-900 to-black border-fuchsia-500/30 text-white' : 'bg-white border-fuchsia-300 text-gray-900'}`}>
        <div className={`sticky top-0 p-3 flex items-center justify-between z-10 border-b ${dark ? 'bg-black/90 border-white/10' : 'bg-white/95 border-black/10'}`}>
          <h3 className="font-bold bg-gradient-to-r from-fuchsia-500 to-pink-500 bg-clip-text text-transparent flex items-center gap-2"><Film className="w-4 h-4 text-fuchsia-500" />{L.editorTitle}</h3>
          <button onClick={onClose} className={`p-1.5 rounded-full ${dark ? 'bg-white/10' : 'bg-black/10'}`} data-testid="video-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 grid sm:grid-cols-2 gap-4">
          <div className="flex justify-center">
            <canvas ref={canvasRef} width={W} height={H} className="rounded-2xl bg-black w-full max-w-[260px] aspect-[9/16]" data-testid="video-editor-canvas" />
          </div>
          <div className="space-y-3">
            <Slot label="top" slot={top} setSlot={setTop} L={L} dark={dark} />
            <Slot label="bottom" slot={bottom} setSlot={setBottom} L={L} dark={dark} />
            <div>
              <div className="flex items-center justify-between text-[11px] opacity-70 mb-1"><span>{L.split}</span><span className="font-semibold">{split}% / {100 - split}%</span></div>
              <input type="range" min={20} max={80} value={split} onChange={e => setSplit(Number(e.target.value))} className="w-full accent-fuchsia-500" data-testid="video-editor-split" />
            </div>
            <textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder={L.middleText} className={inputCls} data-testid="video-editor-text" />
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-2">{L.textColor}<input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-8 h-8 rounded-lg border-0 bg-transparent" data-testid="video-editor-color" /></label>
              <button onClick={() => setMuted(m => !m)} className={`ml-auto px-3 py-1.5 rounded-full flex items-center gap-1 ${dark ? 'bg-white/10' : 'bg-black/10'}`} data-testid="video-editor-mute">{muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}{muted ? L.mute : L.sound}</button>
            </div>
            <button onClick={render} disabled={rendering} className="w-full py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white font-semibold text-xs disabled:opacity-50 flex items-center justify-center gap-2" data-testid="video-editor-render">
              {rendering ? <><Loader2 className="w-4 h-4 animate-spin" />{L.rendering} {progress}%</> : L.render}
            </button>
            {outUrl && (
              <div className="space-y-2" data-testid="video-editor-result">
                <video src={outUrl} controls playsInline className="w-full rounded-xl max-h-48 bg-black" />
                <a href={outUrl} download={`destine-edit.${ext}`} className={`w-full py-2.5 rounded-full text-xs font-semibold flex items-center justify-center gap-2 ${dark ? 'bg-white/10 text-white' : 'bg-black/10 text-gray-900'}`} data-testid="video-editor-download"><Download className="w-4 h-4" />{L.download}</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
